//External libraries
import * as React from "react";
import { useQuery } from "@apollo/client";
import { observer } from "mobx-react";
import { useLocalization } from "@shared-ui/localization-context";
import { useBexApi } from "@shared-ui/bex-api-context";

// Uitk
import { UitkLink } from "uitk-react-link";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkHeading } from "uitk-react-text";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";

// Components-Utilities
import IsomorphicCarousel from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { RecentlyViewedHotelCard } from "./components/RecentlyViewedHotelCard";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { buildVariablesList } from "./utils/buildVariablesList";
import { groupHotelsByDate } from "./utils/groupHotelsByDate";
import { withStores } from "src/stores";

// Types
import {
  RecentlyViewedHotelsProps,
  RecentlyViewedHotelsFlexModuleResult,
  CustomerSearchHistoryQueryComponentProps,
  HotelsGroupedByDates,
  HistoryItem,
  RecentlyViewedHotelsCardQueryComponentProps,
} from "./typings";
import { getFmId } from "src/stores/ExperienceTemplateStore";

import {
  CustomerPropertySearchByIdQueryDocument,
  CustomerSearchHistoryQueryDocument,
  HistoryType,
} from "__generated__/typedefs";

const NUMBER_OF_HOTELS_TO_GET = 6;
const HISTORY_TYPE: HistoryType = "PROPERTY_DETAIL";

export const RecentlyViewedHotels = withStores(
  "flexModuleModelStore",
  "hotels"
)(
  observer((props: RecentlyViewedHotelsProps) => {
    const { templateComponent, flexModuleModelStore, hotels: hotelsStore } = props;
    if (!templateComponent) {
      return null;
    }

    const {
      metadata: { id },
    } = templateComponent;
    const model = flexModuleModelStore.getModel(id) as RecentlyViewedHotelsFlexModuleResult | null;

    /* istanbul ignore next */
    if (!model) {
      return null;
    }

    return (
      <CustomerSearchHistoryQueryComponent
        model={model}
        templateComponent={templateComponent}
        hotelsStore={hotelsStore}
      />
    );
  })
);

const CustomerSearchHistoryQueryComponent = (props: CustomerSearchHistoryQueryComponentProps) => {
  const { model, templateComponent, hotelsStore } = props;
  const { formatText } = useLocalization();
  const { context } = useBexApi();
  const variables = {
    context,
    lobs: HISTORY_TYPE,
    numberOfResults: NUMBER_OF_HOTELS_TO_GET,
  };

  const { data, loading, error } = useQuery(CustomerSearchHistoryQueryDocument, {
    variables,
    ssr: false,
  });

  if (loading || error || !data) {
    return null;
  }

  const customerSearchHistory = data.customer.searchHistory
    ?.reduce((acc, searchHistoryItemsArray) => [...acc, ...searchHistoryItemsArray.items!], [])
    .filter((item) => item) as HistoryItem[];

  /* istanbul ignore next: empty array worst case scenario */
  if (!customerSearchHistory) {
    return null;
  }

  // group by start date - end date
  const hotelsGroupedByDates: HotelsGroupedByDates = groupHotelsByDate(customerSearchHistory);

  const hotelsToQuery = buildVariablesList(hotelsGroupedByDates, context, hotelsStore!);

  const carouselClassContainer = customerSearchHistory.length < 4 ? "carouselFitItemsContainer" : "";
  const { title, seeAllLink } = model;
  const {
    metadata: { id },
    config: { fmTitleId },
  } = templateComponent;
  const fmId = getFmId(templateComponent);

  return (
    <UitkSpacing padding={{ block: "three" }}>
      <div className="RecentlyViewedHotels" id={id} data-fm={fmId} data-fm-title-id={fmTitleId}>
        <UitkSpacing margin={{ blockend: "four" }}>
          <div>
            <UitkHeading tag="h2" size={4}>
              {title}
            </UitkHeading>
            <div data-testid="see-all-recently-viewed-link">
              <UitkLink>
                <TrackedLink moduleName="recently-viewed-hotels" href={seeAllLink} rfrr="link" linkName={seeAllLink}>
                  {formatText("recentlyViewedHotels.seeAlllink")}
                </TrackedLink>
              </UitkLink>
            </div>
          </div>
        </UitkSpacing>
        <UitkSpacing padding={{ blockend: "three" }}>
          <div className={carouselClassContainer}>
            <IsomorphicCarousel
              itemsVisible={{
                lg: 3,
                md: 3,
                sm: 1,
              }}
              pageBy={1}
              peekWidth={50}
              buttonText={{
                nextButton: formatText("carousel.item.next"),
                prevButton: formatText("carousel.item.prev"),
              }}
              wrapItems
            >
              {hotelsToQuery.map(
                /* istanbul ignore next*/ (hotel, index: number) => (
                  <RecentlyViewedHotelCardQueryComponent
                    key={`recently-viewed-hotels-${hotel.destination.propertyIds?.[0]}`}
                    cardIndex={index}
                    customerPropertySearchByIdVariables={hotel}
                    hotelsStore={hotelsStore}
                    customerSearchHistory={customerSearchHistory}
                  />
                )
              )}
            </IsomorphicCarousel>
          </div>
        </UitkSpacing>
      </div>
    </UitkSpacing>
  );
};

export const RecentlyViewedHotelCardQueryComponent = (props: RecentlyViewedHotelsCardQueryComponentProps) => {
  const { cardIndex, customerPropertySearchByIdVariables, hotelsStore, customerSearchHistory } = props;

  const { data, loading, error } = useQuery(CustomerPropertySearchByIdQueryDocument, {
    variables: customerPropertySearchByIdVariables,
    ssr: false,
  });

  if (loading || error || !data) {
    return null;
  }

  const recentViewedHotel = hotelsStore.getFreshRecentlyViewedHotels(customerSearchHistory, data);

  if (recentViewedHotel.length === 0) {
    return null;
  }

  return (
    <UitkLayoutGrid blockSize="full_size">
      <UitkLayoutGridItem>
        <RecentlyViewedHotelCard
          key={`recently-viewed-hotel-${recentViewedHotel[0].hisUrl}`}
          cardIndex={cardIndex}
          recentlyViewedHotel={recentViewedHotel[0]}
        />
      </UitkLayoutGridItem>
    </UitkLayoutGrid>
  );
};

export default RecentlyViewedHotels;
